<template>
  <component
    :is="label ? 'div' : 'hr'"
    role="separator"
    :aria-orientation="orientation"
    class="separator"
    :class="classes"
    :style="style"
  >
    <span
      v-if="label"
      class="separator--label"
    >{{ label }}</span>
  </component>
</template>

<script setup>
import { computed } from 'vue'

defineOptions({ name: 'Separator' })

const spacingMap = { xs: '2px', sm: '4px', md: '8px', lg: '16px', xl: '24px' }
const insetMap = {
  'true': 'inset',
  'item': 'item-inset',
  'item-thumbnail': 'item-thumbnail-inset'
}

const props = defineProps({
  vertical: { type: Boolean, default: false },
  spaced: { type: [Boolean, String], default: false },
  inset: { type: [Boolean, String], default: false },
  color: { type: String, default: null },
  size: { type: String, default: null },
  label: { type: String, default: null },
  labelAlign: { type: String, default: 'center', validator: v => ['left', 'center', 'right'].includes(v) }
})

const orientation = computed(() => props.vertical ? 'vertical' : 'horizontal')

const classes = computed(() => [
  `separator--${orientation.value}`,
  props.inset !== false && `separator--${orientation.value}-${insetMap[props.inset]}`,
  props.color && `separator--color-${props.color}`,
  props.label && `separator--labeled`,
  props.label && `separator--label-${props.labelAlign}`
])

const style = computed(() => {
  const s = {}

  if (props.size) {
    s[props.vertical ? 'width' : 'height'] = props.size
  }

  if (props.spaced !== false) {
    const gap = props.spaced === true
      ? '8px'
      : spacingMap[props.spaced] ?? props.spaced
    const dirs = props.vertical ? ['Left', 'Right'] : ['Top', 'Bottom']
    s[`margin${dirs[0]}`] = s[`margin${dirs[1]}`] = gap
  }

  return s
})
</script>

<style>
@reference "#app-styles.css";

.separator { --_line: rgb(0 0 0 / 0.1); }
.body--dark .separator { --_line: rgb(255 255 255 / 0.1); }
</style>

<style scoped>
@reference "#app-styles.css";

.separator {
  @apply border-0 m-0 p-0;

  &.separator--horizontal {
    @apply w-full h-px;
    background-color: var(--_line);

    &.separator--horizontal-inset            { @apply ml-4; }
    &.separator--horizontal-item-inset       { @apply ml-18; }
    &.separator--horizontal-item-thumbnail-inset { @apply ml-25; }
  }

  &.separator--vertical {
    @apply inline-block self-stretch w-px h-auto;
    background-color: var(--_line);

    &.separator--vertical-inset            { @apply mt-2; }
    &.separator--vertical-item-inset       { @apply mt-4; }
    &.separator--vertical-item-thumbnail-inset { @apply mt-6; }
  }

  &.separator--color-base    { background-color: var(--color-edge-base); }
  &.separator--color-surf    { background-color: var(--color-edge-surf); }
  &.separator--color-float   { background-color: var(--color-edge-float); }
  &.separator--color-overlay { background-color: var(--color-edge-overlay); }

  &.separator--color-primary   { @apply bg-primary-mid/30; }
  &.separator--color-secondary { @apply bg-secondary-mid/30; }
  &.separator--color-positive  { @apply bg-positive-mid/30; }
  &.separator--color-negative  { @apply bg-negative-mid/30; }
  &.separator--color-warning   { @apply bg-warning-mid/30; }
  &.separator--color-info      { @apply bg-info-mid/30; }
  &.separator--color-accent    { @apply bg-accent-mid/30; }

  &.separator--labeled {
    @apply flex items-center gap-3 bg-transparent! h-auto;

    &::before,
    &::after {
      content: '';
      @apply flex-1 h-px;
      background-color: var(--_line);
    }

    &.separator--label-left  { &::before { @apply hidden; } }
    &.separator--label-right { &::after  { @apply hidden; } }
  }

  .separator--label {
    @apply text-xs ink-subtle font-medium whitespace-nowrap;
  }
}
</style>
